import React from 'react';
import { User, UserCheck, MessageSquare } from 'lucide-react';
import { formatTimeStable } from './utils/formatUtils';
import { getStatusColor, getVarianceColor } from './utils/styleUtils';

const WorklogCard = ({ log }) => {
  const hasAuthor = log.author && log.author !== 'null';
  const hasAssignee = log.issue_assignee && log.issue_assignee !== 'null';
  const hasComment = log.comment && log.comment.trim() !== '';
  
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3">
      {/* Header: Issue Key and Status */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="font-medium text-gray-900 dark:text-slate-100 text-sm">{log.issue_key}</div>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs flex-shrink-0 ${getStatusColor(log.issue_status)}`}>
              {log.issue_status}
            </span>
          </div>
          <div className="text-gray-600 dark:text-slate-400 text-xs line-clamp-2" title={log.issue_summary}>
            {log.issue_summary}
          </div>
        </div>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center space-x-2">
          <User className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-slate-400">Author</div>
            <div className="text-blue-600 dark:text-blue-400 font-medium truncate">
              {hasAuthor ? log.author : 'Unknown'}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <UserCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-slate-400">Assignee</div>
            <div className="text-green-600 dark:text-green-400 truncate">
              {hasAssignee ? log.issue_assignee : 'Unassigned'}
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-slate-400">Logged Hours</div>
          <div className="font-medium text-blue-600 dark:text-blue-400">{log.loggedHours}h</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-slate-400">Estimated</div>
          <div className="text-gray-600 dark:text-slate-400">{log.estimatedHours}h</div>
        </div>
      </div>

      {/* Date, Time, Variance */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-white/10">
        <div>
          <div className="text-xs text-gray-500 dark:text-slate-400">Date & Time</div>
          <div className="text-sm text-gray-600 dark:text-slate-400">
            {log.loggedDate} {formatTimeStable(log.started)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 dark:text-slate-400">Variance</div>
          <div className={`text-sm font-medium ${getVarianceColor(log.variance)}`}>
            {log.variance > 0 ? '+' : ''}{log.variance}h
          </div>
        </div>
      </div>

      {/* Project and Description */}
      <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-white/10">
        <div>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-200">
            {log.issue_project_name || log.issue_project_key}
          </span>
        </div>
        {hasComment && (
          <div className="flex items-start space-x-2">
            <MessageSquare className="w-4 h-4 text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
            <div className="text-gray-600 dark:text-slate-400 text-xs flex-1">
              <div className="line-clamp-2" title={log.comment}>
                {log.comment}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorklogCard;

